// 任務狀態枚舉
export type TaskStatus = "OPEN" | "CLOSED";

// 任務類別枚舉
export type TaskCategory = "work" | "family" | "health" | "social" | "personal";

// 優先級枚舉 (1=高, 2=中, 3=低)
export type TaskPriority = "1" | "2" | "3";

// 能量需求枚舉 (1=高, 2=中, 3=低)
export type TaskEnergyLevel = "1" | "2" | "3";

// 關閉原因枚舉 (新增)
export type TaskCloseReason =
  | "completed"
  | "split"
  | "scheduled"
  | "dropped"
  | undefined;

// 判斷任務是否應在 UI 顯示（排除已 Drop 的任務）
export const isVisibleTask = (task: {
  closeReason?: TaskCloseReason;
}): boolean => task.closeReason !== "dropped";

// 過濾器類型
export type TaskFilterType =
  | "all" // 全部任務
  | "today" // 今天
  | "tomorrow" // 明天
  | "week" // 本週
  | "unscheduled" // 未排程
  | "quick" // 快速任務
  | TaskCategory // 按類別篩選
  | `priority-${string}`; // 按優先級篩選

// 任務實體介面
export interface Task {
  id: string; // 唯一識別符
  title: string; // 任務標題(最多50字)
  notes?: string; // 補充說明（可選，支持較長文本）
  status: TaskStatus; // 任務狀態
  createdAt: Date; // 創建時間
  updatedAt?: Date; // 更新時間
  deletedAt?: Date; // 刪除時間
  targetDate?: string; // 目標完成日期（YYYY-MM-DD 格式，可選）
  plan?: string; // 規劃時間窗口（YYYY-MM-DD 格式，可選）
  closedAt?: Date; // 完成時間(可選)
  closeReason?: TaskCloseReason; // 關閉原因（新增）
  migrationCount: number; // 遷移次數
  category?: TaskCategory; // 類別(可選)
  estimatedTime?: number; // 估計完成時間(分鐘,可選)
  priority?: TaskPriority; // 優先級（可選）
  energyLevel?: TaskEnergyLevel; // 所需能量(可選)
  location?: string; // 地點信息（可選）
  relatedTaskID?: string; // 相關任務（可選）
  relatedTaskTitle?: string; // 相關卡片
  originCaptureId?: string; // 來源捕獲ID（如果從捕獲轉換，可選）
  userId: string; // 所屬用戶ID
  tags?: string[]; // 標籤（可選）
  isQuickTask?: boolean; // 是否為快速任務
  scheduledTimeBoxIds?: string[]; // 追蹤此任務被排入哪些 TimeBox（用於重新計算 lastTimeboxedDate）
  lastTimeboxedDate?: string; // 所有關聯 TimeBox 日期中最大的那個（YYYY-MM-DD），用於顯示過濾
  objectiveId?: string; // 關聯的 Objective ID（可選）
}

// 允許可清空欄位傳入 null（null = 明確清空，undefined = 不更新）
export type TaskUpdateData = Omit<Partial<Task>, 'plan' | 'notes' | 'location' | 'targetDate' | 'estimatedTime' | 'category' | 'objectiveId' | 'priority' | 'energyLevel' | 'lastTimeboxedDate'> & {
  plan?: string | null;
  notes?: string | null;
  location?: string | null;
  targetDate?: string | null;
  estimatedTime?: number | null;
  category?: TaskCategory | null;
  objectiveId?: string | null;
  priority?: TaskPriority | null;
  energyLevel?: TaskEnergyLevel | null;
  lastTimeboxedDate?: string | null;
};

// 樂觀更新用的暫時 task（尚未寫入後端，id 為 optimistic-xxx）
export type OptimisticTask = Pick<
  Task,
  | "id"
  | "title"
  | "status"
  | "priority"
  | "userId"
  | "objectiveId"
  | "createdAt"
  | "updatedAt"
  | "migrationCount"
>;

// 用於創建任務的輸入介面 (僅必填字段)
export interface CreateTaskInput {
  title: string;
  category?: TaskCategory;
  targetDate?: string;
  plan?: string; // 規劃時間窗口（YYYY-MM-DD 格式，可選）
  notes?: string;
  priority?: TaskPriority;
  energyLevel?: TaskEnergyLevel;
  estimatedTime?: number;
  location?: string; // 地點
  relatedTaskID?: string;
  tags?: string[];
  userId: string; // 確保用戶ID是必填的
  isQuickTask?: boolean; // 是否為快速任務
  objectiveId?: string; // 關聯的 Objective ID（可選）
}

// 類別顏色配置
export const categoryColors = {
  work: "bg-sky-100 text-sky-800",
  family: "bg-green-100 text-green-800",
  health: "bg-sky-100 text-sky-800",
  social: "bg-pink-100 text-pink-800",
  personal: "bg-yellow-100 text-yellow-800",
};

// 類別邊框顏色配置
export const categoryBorderColors = {
  work: "border-sky-500",
  family: "border-green-500",
  health: "border-sky-500",
  social: "border-pink-500",
  personal: "border-yellow-500",
};

// 類別標籤映射
export const categoryLabels: Record<string, string> = {
  untitled: "無類別任務",
  work: "工作",
  personal: "個人",
  family: "家庭",
  health: "健康",
  social: "關係",
};

/**
 * 任務分析結果介面
 * 用於前後端通信，定義了 AI 任務分析的返回結構
 */
export interface TaskAnalysisResult {
  title: string; // 簡潔明確的任務標題（以動詞開頭，表達行動）
  notes?: string; // 附加說明或背景信息
  category?: TaskCategory; // 任務類別 ("work", "family", "health", "social", "personal", 或 null)
  priority?: TaskPriority; // 優先級 ('1'=高, '2'=中, '3'=低 或空值)
  energyLevel?: TaskEnergyLevel; // 所需專注度（能量）('1'=高, '2'=中, '3'=低 或空值)
  estimatedTime?: number; // 估計完成時間（分鐘）
  targetDate?: string; // 目標日期（如有，格式為 YYYY-MM-DD）
  plan?: string; // 規劃時間窗口（如有，格式為 YYYY-MM-DD）
  location?: string; // 相關地點信息（如有）
  isQuickTask?: boolean; // 是否為快速任務（估計時間不超過15分鐘的小型任務）
  confidence: number; // 分析結果的置信度 (0.0 - 1.0)
}

/**
 * 本地分割任務返回結果(返回 ID 陣列)
 */
export interface SplitTaskLocalResult {
  parentTask: Task;
  subtaskIds: string[];
}

/**
 * Cloud Function AI 分割任務返回結果(返回完整 Task 物件)
 */
export interface SplitTaskResult {
  parentTask: Task;
  subtasks: Task[];
}

/**
 * Cloud Function AI 優化任務內容返回結果
 */
export interface OptimizeTaskResult {
  optimizedTitle: string;
  optimizedNotes: string;
  changesSummary: string;
  suggestedCategory?: TaskCategory;
  suggestedGoalId?: string | null;
}
